/**
 * PRIVACY REQUESTS — the owner's answer to "what do you hold about me?" and
 * "please delete it", for anyone in the building's records.
 *
 * The published privacy policy gives every person the building logs — a
 * visitor who never had an account, a resident, a member of staff — the right
 * to see what is held about them and to have it erased without destroying the
 * building's overall log. Until 2026-09-14 the product could do neither for
 * anyone without a login. The building (the organisation) is the controller,
 * so its OWNER answers the request, from here.
 *
 * ── HOW IT STAYS PRECISE ──
 * Search returns each record that names the person, as recorded, with a
 * checkbox. Export and erase act only on what is ticked. Erasing also requires
 * the name to be typed again, and the server re-runs the same search inside the
 * erase and refuses anything that no longer matches — so a stale screen cannot
 * erase the wrong record. On a visit shared with other people only this
 * person's name is removed; the visit and everyone else on it stay.
 *
 * Backend: src/CRUD/privacy.js + migration 102 (lobbero_be).
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { apiRoute } from '../../../constants/api-constants';
import toast from '../../../utils/toast';
import { AppButton } from '../../button/app-button';
import { ConfirmDialog } from '../../modal/confirm-dialog';
import { Field, Input, Label } from '../../onboarding/onboarding.styled';
import { SectionHeading, SectionLead } from './account.styled';
import { formatDate } from '../../../function-utils/locale';
import {
  layout,
  palette,
  radius,
  spacing,
  status,
  transition,
  typography,
} from '../../../style-utils/theme';
import { up } from '../../../style-utils/media-queries';

interface PrivacyMatch {
  ref: string;
  kind: string;
  when: string | null;
  building: string | null;
  name: string;
  erasable: boolean;
}

const KIND_LABEL: Record<string, string> = {
  visit: 'Visit',
  activity: 'Activity',
  parcel: 'Parcel',
  'resident record': 'Resident record',
  account: 'Account',
  invitation: 'Invitation',
  mention: 'Named in a note',
  'earlier version': 'Earlier version',
  // Lobbero Stays plan S2: a stay guest's verified identity (src/CRUD/privacy.js).
  'stay guest': 'Stay guest',
  // Lobbero Stays plan S3: a short stay the person is the lead guest of.
  'short stay': 'Short stay',
};

/* The same comparison the server makes: accents, case and spacing ignored. */
const norm = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const serverMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } } }).response?.data
    ?.message || fallback;

const Block = styled.section`
  margin-top: ${spacing.lg};
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
`;

const BlockTitle = styled.h3`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const BlockText = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

/* Phone first: the field and the button stack; side by side from md up. */
const SearchForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  ${up('md')} {
    flex-direction: row;
    align-items: flex-end;
    gap: ${spacing.sm};

    & > div {
      flex: 1;
      margin-bottom: 0;
    }
  }
`;

const CountLine = styled.p`
  margin: ${spacing.md} 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.inkSoft};
`;

const ResultList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;

/* Each result is one label, so the whole row is the tap target (≥ 44px). */
const ResultRow = styled.label<{ $disabled: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${spacing.sm};
  align-items: start;
  min-height: ${layout.touchTarget};
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: ${transition.colors};

  &:hover {
    background: ${({ $disabled }) =>
      $disabled ? palette.surface : palette.irisTintSoft};
  }

  input {
    width: ${spacing.md};
    height: ${spacing.md};
    margin: 0;
    accent-color: ${palette.teal};
  }
`;

const ResultName = styled.span`
  display: block;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const ResultMeta = styled.span`
  display: block;
  margin-top: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const KindPill = styled.span`
  display: inline-block;
  margin-right: ${spacing.xs};
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${status.neutral.bg};
  color: ${status.neutral.fg};
  font-weight: ${typography.weight.semibold};
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin-top: ${spacing.sm};
`;

const Refusal = styled.p`
  margin: ${spacing.sm} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${status.alert.fg};
`;

// The reader's own clock (R-T6 — a privacy request belongs to no building),
// under the product locale; it used the browser's.
const formatWhen = (when: string | null): string =>
  when ? formatDate(when, { year: 'numeric', month: 'short', day: 'numeric' }) : '';

export const AccountPrivacy: React.FC = () => {
  const [query, setQuery] = useState('');
  const [searched, setSearched] = useState<string | null>(null);
  const [matches, setMatches] = useState<PrivacyMatch[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [searching, setSearching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const erasableCount = useMemo(
    () => matches.filter(m => m.erasable).length,
    [matches]
  );
  const confirmed =
    searched !== null && norm(confirmName) === norm(searched) && selected.size > 0;

  const search = async (name: string) => {
    setSearching(true);
    setRefusal(null);
    try {
      const { data } = await axios.post(`${apiRoute}/privacy/search`, { name });
      const found: PrivacyMatch[] = data.matches || [];

      setMatches(found);
      /* NOTHING TICKED. Every result used to start selected, so one click of
         "Erase selected" also removed the other people who share the name. */
      setSelected(new Set());
      setSearched(name);
      setConfirmName('');
    } catch (error) {
      setRefusal(serverMessage(error, 'We could not search the records. Please try again.'));
    } finally {
      setSearching(false);
    }
  };

  const onSearch = (event: React.FormEvent) => {
    event.preventDefault();
    if (!searching) search(query.trim());
  };

  const toggle = (ref: string) =>
    setSelected(prev => {
      const next = new Set(prev);

      if (next.has(ref)) next.delete(ref);
      else next.add(ref);

      return next;
    });

  const toggleAll = () =>
    setSelected(
      selected.size === erasableCount
        ? new Set()
        : new Set(matches.filter(m => m.erasable).map(m => m.ref))
    );

  /* Saved from a blob, like "Download my data": the route is
     cookie-authenticated and returns JSON. */
  const exportRecords = async () => {
    if (exporting || searched === null) return;
    setExporting(true);
    setRefusal(null);
    try {
      const response = await axios.post(
        `${apiRoute}/privacy/export`,
        { name: searched, refs: Array.from(selected) },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.download = 'lobbero-privacy-export.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('The export has been downloaded.');
    } catch {
      setRefusal('We could not prepare the export. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const erase = async () => {
    if (erasing || searched === null) return;
    setErasing(true);
    setRefusal(null);
    try {
      const { data } = await axios.post(`${apiRoute}/privacy/erase`, {
        name: searched,
        confirm: confirmName,
        refs: Array.from(selected),
      });

      setConfirmOpen(false);
      toast.success(
        `${data.erased} ${data.erased === 1 ? 'record' : 'records'} erased.`
      );
      await search(searched);
    } catch (error) {
      setConfirmOpen(false);
      setRefusal(serverMessage(error, 'Nothing was erased. Please try again.'));
    } finally {
      setErasing(false);
    }
  };

  return (
    <>
      <SectionHeading>Privacy requests</SectionHeading>
      <SectionLead>
        When someone asks what your building holds about them, or asks for it to
        be deleted — a visitor, a resident or a member of staff — find their
        records here, download a copy for them, or erase them.
      </SectionLead>

      <Block>
        <BlockTitle>Find a person</BlockTitle>
        <BlockText>
          Searches every visit, activity, parcel, resident record, account and
          invitation in your organisation, the notes on them, and their earlier
          versions. Every word you type must be a whole word of the name (accents
          and capitals are ignored), or type the exact email address. Tick only
          the records that are about this person.
        </BlockText>
        <SearchForm onSubmit={onSearch} role="search">
          <Field>
            <Label htmlFor="privacy-name">Person’s name</Label>
            <Input
              id="privacy-name"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="First and last name"
              autoComplete="off"
            />
          </Field>
          <AppButton
            type="submit"
            variant="secondary"
            loading={searching}
            loadingLabel="Searching"
            disabled={query.trim().length < 3}
          >
            Search
          </AppButton>
        </SearchForm>

        {searched !== null ? (
          <>
            <CountLine aria-live="polite">
              {matches.length === 0
                ? `Nothing in your organisation names “${searched}”.`
                : `${matches.length} ${matches.length === 1 ? 'record names' : 'records name'} “${searched}”.`}
            </CountLine>
            {matches.length > 0 ? (
              <>
                <ResultList>
                  {matches.map(m => (
                    <li key={m.ref}>
                      <ResultRow $disabled={!m.erasable}>
                        <input
                          type="checkbox"
                          checked={selected.has(m.ref)}
                          disabled={!m.erasable}
                          onChange={() => toggle(m.ref)}
                        />
                        <span>
                          <ResultName>{m.name}</ResultName>
                          <ResultMeta>
                            <KindPill>{KIND_LABEL[m.kind] || m.kind}</KindPill>
                            {[formatWhen(m.when), m.building].filter(Boolean).join(' · ')}
                            {!m.erasable ? ' · the owner account cannot be erased here' : ''}
                          </ResultMeta>
                        </span>
                      </ResultRow>
                    </li>
                  ))}
                </ResultList>
                <Actions>
                  {erasableCount > 1 ? (
                    <AppButton variant="ghost" size="compact" onClick={toggleAll}>
                      {selected.size === erasableCount ? 'Clear selection' : 'Select all'}
                    </AppButton>
                  ) : null}
                  <AppButton
                    variant="secondary"
                    size="compact"
                    loading={exporting}
                    loadingLabel="Preparing the export"
                    disabled={selected.size === 0}
                    onClick={exportRecords}
                  >
                    Download export
                  </AppButton>
                </Actions>
              </>
            ) : null}
          </>
        ) : null}
        {refusal ? <Refusal role="alert">{refusal}</Refusal> : null}
      </Block>

      {searched !== null && erasableCount > 0 ? (
        <Block>
          <BlockTitle>Erase the selected records</BlockTitle>
          <BlockText>
            Removes this person’s name from each selected record, together with
            every earlier version of it. Visits shared with other people keep
            everyone else. This cannot be undone. To confirm, type the name
            exactly as you searched it.
          </BlockText>
          <Field>
            <Label htmlFor="privacy-confirm">Type “{searched}” to confirm</Label>
            <Input
              id="privacy-confirm"
              value={confirmName}
              onChange={e => setConfirmName(e.target.value)}
              autoComplete="off"
            />
          </Field>
          <AppButton
            variant="destructive"
            size="compact"
            disabled={!confirmed || erasing}
            onClick={() => setConfirmOpen(true)}
          >
            Erase selected
          </AppButton>
        </Block>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title="Erase these records?"
        message={`${selected.size} ${selected.size === 1 ? 'record' : 'records'} naming this person will be erased, with every earlier version of them. This cannot be undone.`}
        primaryLabel={erasing ? 'Erasing…' : 'Erase records'}
        primaryVariant="destructive"
        dismissLabel="Keep them"
        onPrimary={erase}
        onDismiss={() => setConfirmOpen(false)}
      />
    </>
  );
};
